/**
 * Application configuration.
 *
 * Everything here starts out populated from the `VITE_*` build-time env vars
 * (see `.env.example`), then `loadRuntimeConfig()` overlays whatever
 * `/config.json` provides on top of that — only keys present there with a
 * non-empty value override their `VITE_*` default. This lets one built image
 * be repointed at a different backend/auth/maps setup by mounting a
 * `config.json` file (e.g. a Kubernetes ConfigMap), without rebuilding.
 */
export interface AppConfig {
  backendBaseUrl: string;
  authUrl: string;
  authRealm: string;
  authClientId: string;
  mapsBaseUrl: string;
  mapsApiKey: string;
}

export const config: AppConfig = {
  backendBaseUrl: import.meta.env.VITE_BACKEND_BASE_URL ?? "",
  authUrl: import.meta.env.VITE_AUTH_URL ?? "",
  authRealm: import.meta.env.VITE_AUTH_REALM ?? "",
  authClientId: import.meta.env.VITE_AUTH_CLIENT_ID ?? "",
  mapsBaseUrl: import.meta.env.VITE_LITTLE_MONKEY_MAPS_BASE_URL ?? "",
  mapsApiKey: import.meta.env.VITE_LITTLE_MONKEY_API_KEY ?? "",
};

/**
 * Fetches `/config.json` and overlays it onto `config`. Awaited once at
 * startup, before anything that reads `config` runs (see `main.ts`).
 *
 * A missing `/config.json` (a plain dev setup with no runtime overrides) is
 * not an error — it just leaves the `VITE_*` build-time defaults in place.
 * An unset key within the file behaves the same way: only keys present with
 * a non-empty value override their default.
 */
export async function loadRuntimeConfig(): Promise<void> {
  let response: Response;
  try {
    response = await fetch("/config.json", { cache: "no-store" });
  } catch (error) {
    console.error("[config] Failed to fetch /config.json", error);
    return;
  }

  if (!response.ok) {
    return;
  }

  let runtimeValues: Partial<Record<keyof AppConfig, string>>;
  try {
    runtimeValues = await response.json();
  } catch (error) {
    console.error("[config] /config.json is not valid JSON", error);
    return;
  }

  for (const [key, value] of Object.entries(runtimeValues)) {
    if (value) {
      config[key as keyof AppConfig] = value;
    }
  }
}
